import type {
    WorkflowEditFormStepData,
    WorkflowEditFormStepType,
} from "@/components/automations/automation-edit-form-data";
import { atom, type PrimitiveAtom } from "jotai";
import { atomEffect } from "jotai-effect";
import { v4 as uuidv4 } from "uuid";

/**
 * Shape of the step used by the editor for drafting.
 */

export type DraftPromptStep = {
    type: "prompt";
    id: string;
    content: PrimitiveAtom<string>;
};

export type DraftCommandStep = {
    type: "command";
    id: string;
    content: PrimitiveAtom<string>;
};

export type DraftPullRequestStep = {
    type: "pr";
    id: string;
    title: PrimitiveAtom<string>;
    description: PrimitiveAtom<string>;
    branch: PrimitiveAtom<string>;
    isDraft: PrimitiveAtom<boolean>;
};

export type DraftStep = DraftPromptStep | DraftCommandStep | DraftPullRequestStep;

/**
 * Stores the steps that are currently being edited in the automation details page.
 * is null until the workflow steps are available.
 */
export const editableStepsAtom = atom<WorkflowEditFormStepData[] | null>(null);

/**
 * An atom effect that automatically creates a new step if the steps array is empty.
 */
export const createStepIfEmptyAtomEffect = atomEffect((get, set) => {
    const steps = get(editableStepsAtom);
    if (steps && steps.length === 0) {
        set(createNewStepAtom, "command");
    }
});

/**
 * This stores the container element that contains the step list and edit area.
 * This is needed to calculate the y position of the step node that is currently being edited.
 * We don't want the node editor card to overflow the parent container. we need the bottom position of the container to calculate the max y position of the node editor card.
 */
export const stepListContainerAtom = atom<HTMLDivElement | null>(null);

/**
 * A writable atom that replaces an automation step in the steps array with the given step at the given index.
 */
export const replaceStepAtom = atom(
    null,
    (get, set, { index, step }: { index: number; step: WorkflowEditFormStepData }) => {
        const steps = get(editableStepsAtom);
        if (steps) {
            set(editableStepsAtom, [...steps.slice(0, index), step, ...steps.slice(index + 1)]);
        }
    },
);

/**
 * A writable atom that removes an automation step from the steps array.
 */
export const removeStepAtom = atom(null, (get, set, index: number) => {
    const steps = get(editableStepsAtom);
    if (steps) {
        set(
            editableStepsAtom,
            steps.filter((_, i) => i !== index),
        );
        const selectedIndex = get(stepIndexBeingEditedAtom);
        if (selectedIndex !== null) {
            if (index < selectedIndex) {
                // if the step being edited is after the removed step, decrement the index
                set(stepIndexBeingEditedAtom, selectedIndex - 1);
            } else if (index === selectedIndex) {
                // the step being edited is the removed step, reset edit state
                set(stepIndexBeingEditedAtom, null);
            }
            // Reset Y position so it gets recalculated after DOM updates
            set(stepNodeYPositionAtom, null);
        }
    }
});

/**
 * Indicates whether the automation steps are currently being edited.
 * When true, the action buttons should show cancel/save instead of edit/run.
 * When set to false, all the changes made to the step being edited are discarded if not saved
 * to the steps array.
 */
export const isEditingStepsAtom = atom(false, (_get, set, isEditing: boolean) => {
    set(isEditingStepsAtom, isEditing);
    if (!isEditing) {
        set(newStepAtom, null);
        set(stepIndexBeingEditedAtom, null);
        set(stepNodeYPositionAtom, null);
    }
});

/**
 * The index of the step that is currently being edited.
 * null if no step is being edited.
 * Set this atom to the index of the step that you want to edit.
 * Set this to null to stop editing a step.
 * Prefer {@link stepBeingEditedAtom} to get the step that is currently being edited.
 *
 * @see stepBeingEditedAtom
 */
export const stepIndexBeingEditedAtom = atom<number | null>(null);

/**
 * Stores the new step that is being created.
 */
export const newStepAtom = atom<WorkflowEditFormStepData | null>(null);

/**
 * A setter atom that creates a new empty step of the given type, or "command" if no type is provided.
 * If there is a new step already, calling this will discard and replace the existing step with a new one of the given type.
 */
export const createNewStepAtom = atom(null, (_get, set, type: WorkflowEditFormStepType = "command") => {
    set(stepIndexBeingEditedAtom, null);
    switch (type) {
        case "prompt":
        case "command":
            set(newStepAtom, {
                id: uuidv4(),
                type,
                content: "",
            });
            break;
        case "pr":
            set(newStepAtom, {
                id: uuidv4(),
                type,
                title: "",
                description: "",
                branch: "",
                isDraft: false,
            });
    }
});

/**
 * Indicates whether a new step is being created.
 */
export const isCreatingStepAtom = atom((get) => get(stepIndexBeingEditedAtom) === null && get(newStepAtom) !== null);

export const draftStepAtom = atom<DraftStep | null>(null);

/**
 * An effect that creates a draft step for use in the editor
 * Derived from the selected step index that is being edited or the new step that is being created.
 */
export const createDraftAtomEffect = atomEffect((get, set) => {
    const index = get(stepIndexBeingEditedAtom);
    const newStep = get(newStepAtom);
    const step = index !== null ? get(editableStepsAtom)?.[index] : null;
    const sourceStep = step ?? newStep;
    if (sourceStep) {
        switch (sourceStep.type) {
            case "prompt":
            case "command":
                set(draftStepAtom, {
                    id: sourceStep.id,
                    type: sourceStep.type,
                    content: atom(sourceStep.content),
                });
                break;
            case "pr":
                set(draftStepAtom, {
                    id: sourceStep.id,
                    type: "pr",
                    title: atom(sourceStep.title),
                    description: atom(sourceStep.description),
                    branch: atom(sourceStep.branch),
                    isDraft: atom(sourceStep.isDraft),
                });
        }
    } else {
        set(draftStepAtom, null);
    }
});

export const changeDraftStepTypeAtom = atom(null, (get, set, type: WorkflowEditFormStepType) => {
    const draftStep = get(draftStepAtom);
    if (!draftStep) {
        return;
    }

    const currentType = draftStep.type;
    if (currentType !== "pr" && type !== "pr") {
        // switching from and to a non pr type, can preserve existing content
        set(draftStepAtom, {
            ...draftStep,
            type,
        });
    } else {
        switch (type) {
            case "command":
            case "prompt":
                set(draftStepAtom, {
                    id: draftStep.id,
                    type,
                    content: atom(""),
                });
                break;
            case "pr":
                set(draftStepAtom, {
                    id: draftStep.id,
                    type,
                    title: atom(""),
                    description: atom(""),
                    branch: atom(""),
                    isDraft: atom(false),
                });
        }
    }
});

/**
 * Finalizes and either:
 * - Inserts the draft into the steps array if no step is currently being edited
 * - Replaces the step at the current edit index with the draft if a step is currently being edited
 */
export const upsertDraftStepIntoStepsAtom = atom(null, (get, set) => {
    const draftStep = get(draftStepAtom);
    if (!draftStep) {
        return;
    }

    let newStep: WorkflowEditFormStepData;
    switch (draftStep.type) {
        case "command":
        case "prompt":
            newStep = {
                id: draftStep.id,
                type: draftStep.type,
                content: get(draftStep.content),
            };
            break;
        case "pr":
            newStep = {
                id: draftStep.id,
                type: draftStep.type,
                title: get(draftStep.title),
                description: get(draftStep.description),
                branch: get(draftStep.branch),
                isDraft: get(draftStep.isDraft),
            };
            break;
    }

    const editIndex = get(stepIndexBeingEditedAtom);

    if (editIndex !== null) {
        set(replaceStepAtom, { index: editIndex, step: newStep });
        set(stepIndexBeingEditedAtom, null);
        set(stepNodeYPositionAtom, null);
    } else {
        const steps = get(editableStepsAtom);
        set(newStepAtom, null);
        set(editableStepsAtom, steps ? [...steps, newStep] : [newStep]);
        // once the step is added, keep the editor open by setting it to edit mode
        set(stepIndexBeingEditedAtom, steps ? steps.length : 0);
        set(stepNodeYPositionAtom, null);
    }
});

/**
 * The y position (top offset) of the step node that is currently being edited.
 * Used to align the edit area with the step node.
 * null if no step is being edited.
 */
export const stepNodeYPositionAtom = atom<number | null>(null);
